import React, { useState, useMemo } from 'react';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import InfoAlerts from '../InfoAlerts';
import StockForm from '../components/StockForm';
import ModernStockForm from '../components/ModernStockForm';
import StockTable from '../components/StockTable';
import HowToMessage from '../components/HowToMessage';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { validateInputs, calculateStockData, saveRowsToLocalStorage, loadRowsFromLocalStorage } from '../utils/stockUtils';
import { Helmet } from 'react-helmet-async';

const StockAverage = () => {
    const [formData, setFormData] = useState({ stockName: '', holdUnit: '', avgPrice: '', newAmount: '', currPrice: '' });
    const [rows, setRows] = useState(() => loadRowsFromLocalStorage());
    const [error, setError] = useState('');
    const [editingIndex, setEditingIndex] = useState(null);
    const [infoMessage, setInfoMessage] = useState('');
    const [formView, setFormView] = useState(() => localStorage.getItem('formView') || 'modern');

    const handleViewChange = (event, newValue) => {
        setFormView(newValue);
        localStorage.setItem('formView', newValue);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleReset = () => {
        setFormData({ stockName: '', holdUnit: '', avgPrice: '', newAmount: '', currPrice: '' });
        setEditingIndex(null);
        setError('');
        setInfoMessage('');
    };

    const handleAddOrEditRow = (e) => {
        e.preventDefault();
        const validationError = validateInputs(formData);
        if (validationError) {
            setError(validationError);
            return;
        }
        setError('');
        setInfoMessage(''); // Clear any previous info message

        try {
            const stockNameLower = formData.stockName.toLowerCase();
            const existingStockIndex = rows.findIndex(row => row.stockName.toLowerCase() === stockNameLower);

            if (existingStockIndex !== -1) {
                if (editingIndex !== null && existingStockIndex === editingIndex) {
                    // Editing the same stock, proceed with update
                    const updatedRows = rows.map((row, index) =>
                        index === editingIndex ? calculateStockData(formData) : row
                    );
                    setRows(updatedRows);
                    saveRowsToLocalStorage(updatedRows);
                    setEditingIndex(null);
                    setFormData({ stockName: '', holdUnit: '', avgPrice: '', newAmount: '', currPrice: '' });
                } else {
                    // Duplicate stock exists, show info message
                    setInfoMessage('This stock already exists. Please update the existing one or use a different name.');
                    return; // Prevent adding a new row
                }
            } else {
                if (editingIndex !== null) {
                    // Stock name changed during edit, update the row
                    const updatedRows = rows.map((row, index) =>
                        index === editingIndex ? calculateStockData(formData) : row
                    );
                    setRows(updatedRows);
                    saveRowsToLocalStorage(updatedRows);
                    setEditingIndex(null);
                    setFormData({ stockName: '', holdUnit: '', avgPrice: '', newAmount: '', currPrice: '' });
                } else {
                    // Adding a new stock
                    const newRow = calculateStockData(formData);
                    const updatedRows = [...rows, newRow];
                    setRows(updatedRows);
                    saveRowsToLocalStorage(updatedRows);
                    setFormData({ stockName: '', holdUnit: '', avgPrice: '', newAmount: '', currPrice: '' });
                }

            }
        } catch (error) {
            console.error('Error handling row addition or edit:', error);
            setError('An unexpected error occurred. Please try again.');
        }
    };

    const handleEditRow = (index) => {
        setEditingIndex(index);
        setFormData(rows[index]);
    };

    const handleDeleteRow = (index) => {
        const updatedRows = rows.filter((_, i) => i !== index);
        setRows(updatedRows);
        saveRowsToLocalStorage(updatedRows);
    };

    const totalInvested = useMemo(() => rows.reduce((acc, row) => acc + row.currInvest, 0), [rows]);

    return (
        <Container maxWidth="lg">
            <Helmet>
                <title>Kaliper - Stock Average Calculator | Hearten</title>
                <meta name="description" content="Use the free Kaliper stock average calculator by Hearten. Easily track investments, calculate average prices, and manage your portfolio." />
            </Helmet>
            <Typography variant="h4" component="h2" sx={{ my: 4 }}>
                Stock Average Calculator
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                <InfoAlerts heading={'How to Use'} message={<HowToMessage />} buttonName="How to Use?" />
                <Tabs value={formView} onChange={handleViewChange} aria-label="form view tabs" sx={{ bgcolor: 'background.paper', borderRadius: 1, ml: 'auto' }}>
                    <Tab label="Modern" value="modern" />
                    <Tab label="Classic" value="classic" />
                </Tabs>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {infoMessage && <Alert severity="info" sx={{ mb: 2 }}>{infoMessage}</Alert>}

            {formView === 'modern' ? (
                <ModernStockForm
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleAddOrEditRow={handleAddOrEditRow}
                    handleReset={handleReset}
                    editingIndex={editingIndex}
                />
            ) : (
                <StockForm
                    formData={formData}
                    handleInputChange={handleInputChange}
                    handleAddOrEditRow={handleAddOrEditRow}
                    handleReset={handleReset}
                    editingIndex={editingIndex}
                />
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 5, mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" component="h3">
                    Decision Table
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="h6" component="span">
                        Total Current Investment:
                    </Typography>
                    <Box 
                        sx={{ 
                            bgcolor: 'action.selected',
                            border: '1px solid',
                            borderColor: 'divider',
                            px: 2,
                            py: 0.5,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        <Typography variant="h6" component="span" sx={{ fontWeight: 'bold' }}>
                            ₹{totalInvested}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ overflowX: 'auto', width: '100%' }}>
                <StockTable
                    rows={rows}
                    handleEditRow={handleEditRow}
                    handleDeleteRow={handleDeleteRow}
                />
            </Box>
        </Container>
    );
};

export default StockAverage;